import { Box, CircularProgress, Paper } from '@mui/material'
import images from 'assets/images'
import InvitationForm from 'components/pages/invitation/InvitationForm'
import { useState } from 'react'
import useEffectOnce from 'hooks/useEffectOnce'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'providers/axios'
import useShowError from 'hooks/useShowError'
import utils from 'helpers/utils'
import { URL_APP } from 'helpers/config'
import { AuthPayload } from 'helpers/types'
import { Product, User } from 'type/types'

export type InvitationData = {
  user: Pick<User, 'email' | 'firstName' | 'lastName'> | null
  isNewUser: boolean
  companieId: string
  product: Pick<Product, 'id' | 'name' | 'nameFile'> | null
} & ({ mode: 'DEFAULT' } | { mode: 'REVIEW' })

const Invitation = () => {
  const { invitationToken } = useParams()
  const showError = useShowError()
  const navigate = useNavigate()

  const [invitationData, setInvitationData] = useState<InvitationData>({
    mode: 'DEFAULT',
    user: null,
    isNewUser: false,
    companieId: '',
    product: null,
  })
  const [loading, setLoading] = useState(true)

  useEffectOnce(() => {
    const fetchInvitationData = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get<InvitationData>(`/invitation/${invitationToken}`)
        setInvitationData(data)
        if (!data.isNewUser) {
          await acceptInvitation(data, null)
        } else {
          setLoading(false)
        }
      } catch (error) {
        showError(error)
        navigate('/login')
      }
    }
    fetchInvitationData()
  })

  const acceptInvitation = async (
    invitationData: InvitationData,
    formFields: { firstName: string; lastName: string; password: string } | null
  ) => {
    const redirectUrl = `${URL_APP}/?companyContext=${invitationData.companieId}`

    const { user } = invitationData
    if (!user) return
    try {
      const authDevice = utils.getFullAuthDeviceData()
      const res = await axios.post<AuthPayload>('/invitation/accept', {
        firstName: formFields?.firstName,
        lastName: formFields?.lastName,
        password: formFields?.password,
        authDevice,
        invitationToken,
      })
      if (res.data) {
        const authDevice = res.data.authDevice
        utils.setChromeExtension(authDevice.deviceToken)
        window.location.href = redirectUrl
      }
    } catch (error) {
      showError(error)
      navigate('/login')
    }
  }

  if (!invitationToken) {
    showError('Missing invitation token')
    navigate('/login')
    return null
  }

  return (
    <Box p="24px" bgcolor="#faf8f7" minHeight="100vh">
      <Box maxWidth="433px" mx="auto" textAlign="center">
        <div style={{ marginBottom: '10px' }}>
          <img src={images.LOGO_NN_BLUE} alt="logo" style={{ width: '250px', height: 'auto', marginBottom: '10px' }} />
          <div>
            {invitationData.product && (
              <img
                src={utils.getUrlFileMedia(invitationData.product.nameFile)}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '18px',
                  border: '1px solid var(--black)',
                }}
              />
            )}
          </div>
        </div>
        <Paper variant="outlined" sx={{ p: '20px' }}>
          {loading && <CircularProgress size={40} />}

          {!loading && (
            <>
              <h4 className="fs-21 fw-400 m-0" style={{ marginBottom: '24px' }}>
                {invitationData.mode === 'DEFAULT' && 'Accept invitation to NachoNacho'}
                {invitationData.mode === 'REVIEW' &&
                  invitationData.product &&
                  `Accept invitation to write a review for ${invitationData.product.name}`}
              </h4>

              <InvitationForm
                data={invitationData}
                acceptInvitation={(formFields) => acceptInvitation(invitationData, formFields)}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default Invitation
